import React from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ErrorBoundary – prevents React crashes from leaving a blank screen.
 * Shows a readable Swedish error message, the technical error (so the user
 * can copy/paste it to the admin), and reports it to the backend for logging.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
    this.setState({ info });
    // Best-effort client error reporting (no auth header required).
    try {
      axios.post(`${BACKEND_URL}/api/client-error`, {
        message: String(error?.message || error),
        stack: String(error?.stack || ""),
        component_stack: String(info?.componentStack || ""),
        url: typeof window !== "undefined" ? window.location.href : "",
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      }, { timeout: 5000 });
    } catch {
      /* ignore reporting failures */
    }
    // Self-healing: for known-recoverable errors (stale cache after deploy,
    // chunk-load failures, DOM Portal NotFoundError on iOS Safari), auto-reload
    // once. Reuses the sessionStorage guard set in index.html to prevent loops.
    try {
      if (typeof window !== "undefined" && typeof window.__mvstatsTrySelfHeal === "function") {
        window.__mvstatsTrySelfHeal(error);
      }
    } catch {
      /* ignore */
    }
  }

  copyDetails = async () => {
    const { error, info } = this.state;
    const text = [
      `URL: ${typeof window !== "undefined" ? window.location.href : ""}`,
      `UA: ${typeof navigator !== "undefined" ? navigator.userAgent : ""}`,
      `Fel: ${error?.message || error}`,
      "",
      "Stack:",
      error?.stack || "",
      "",
      "Component stack:",
      info?.componentStack || "",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(text);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch {
      /* ignore */
    }
  };

  render() {
    if (this.state.hasError) {
      const msg = this.state.error?.message || String(this.state.error || "Okänt fel");
      return (
        <div
          data-testid="app-error-boundary"
          className="min-h-screen flex flex-col items-center justify-center px-6 py-10 text-center"
        >
          <div className="max-w-xl space-y-4 w-full">
            <div className="font-display font-black uppercase tracking-widest text-2xl text-white">
              Oj, något gick fel
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed">
              Vi kunde inte visa den här sidan. Prova att ladda om — om problemet
              kvarstår, hör av dig till admin.
            </p>
            <div
              data-testid="app-error-message"
              className="text-left bg-black border border-[#27272A] p-3 text-[11px] text-zinc-300 font-mono-num overflow-auto max-h-40 whitespace-pre-wrap break-words"
            >
              {msg}
            </div>
            <div className="pt-2 flex items-center justify-center gap-2 flex-wrap">
              <button
                type="button"
                data-testid="app-error-reload"
                onClick={() => window.location.reload()}
                className="h-11 px-5 border border-[#34C759] bg-transparent hover:bg-[#34C759]/10 text-[#34C759] font-display uppercase tracking-widest text-sm"
              >
                Ladda om sidan
              </button>
              <button
                type="button"
                data-testid="app-error-copy"
                onClick={this.copyDetails}
                className="h-11 px-5 border border-[#27272A] bg-transparent hover:bg-[#171717] text-white font-display uppercase tracking-widest text-sm"
              >
                {this.state.copied ? "Kopierat!" : "Kopiera detaljer"}
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
